import React, { useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import MyAppointmentsSection from '../../components/employee/components/MyAppointments/MyAppointmentsSection';
import { Appointment, appointmentKey, BusinessConfig, Employee } from '../../types';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { useAdminData } from '../../contexts/AdminDataContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentBusinessConfig } from '../../firebase/firebase-delegate';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';
import { hydrateAppointmentsIntoCache } from '../../utils/cache-delegate';
import { WEBSITE_ID } from '../../projectConfig';
import { dayjs } from '../../utils/dayjs-config';

const MyAppointmentsScreen: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    currentEmployeeQuery: employeeQuery,
    employeesQuery,
    hydrateAppointmentsQuery,
    myAppointments
  } = useAdminData();
  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
  const currentEmployee = employeeQuery.data ?? null;
  const employees = employeesQuery.data ?? [];

  const scopedEmployees = useMemo<Employee[]>(() => {
    if (!isResourceMode) {
      return [];
    }
    return employees.filter((employee) => employee.id !== ownerId);
  }, [employees, isResourceMode, ownerId]);

  const resourceAppointmentsMap = useMemo<Record<string, string[]>>(() => {
    if (!isResourceMode) {
      return {};
    }
    const fourWeeksAgo = dayjs.tz().startOf('day').subtract(4, 'week');
    const idsByDate = new Map<string, Set<string>>();
    scopedEmployees.forEach((employee) => {
      Object.entries(employee.appointments ?? {}).forEach(([dateKey, ids]) => {
        if (!Array.isArray(ids)) return;
        const appointmentDate = dayjs(dateKey, 'MMDDYYYY');
        if (!appointmentDate.isValid() || appointmentDate.isBefore(fourWeeksAgo)) {
          return;
        }
        const existing = idsByDate.get(dateKey) ?? new Set<string>();
        ids.forEach((id) => {
          if (typeof id === 'string' && id.trim().length > 0) {
            existing.add(id);
          }
        });
        idsByDate.set(dateKey, existing);
      });
    });
    const nextMap: Record<string, string[]> = {};
    idsByDate.forEach((ids, dateKey) => {
      nextMap[dateKey] = Array.from(ids);
    });
    return nextMap;
  }, [isResourceMode, scopedEmployees]);

  const resourceAppointmentIds = useMemo<string[]>(() => {
    if (!isResourceMode) {
      return [];
    }
    const ids = new Set<string>();
    Object.values(resourceAppointmentsMap).forEach((dateIds) => {
      dateIds.forEach((id) => ids.add(id));
    });
    return Array.from(ids).sort();
  }, [isResourceMode, resourceAppointmentsMap]);

  const hydrateResourceAppointmentsQuery = useQuery({
    queryKey: ['hydrateResourceAppointments', WEBSITE_ID, resourceAppointmentIds.join('|')],
    enabled: isResourceMode && resourceAppointmentIds.length > 0,
    queryFn: async () => {
      await hydrateAppointmentsIntoCache(queryClient, resourceAppointmentIds, WEBSITE_ID);
      return true;
    },
    staleTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isLoading = employeeQuery.isLoading ||
    employeesQuery.isLoading ||
    businessConfigQuery.isLoading ||
    (isResourceMode ? hydrateResourceAppointmentsQuery.isLoading : hydrateAppointmentsQuery.isLoading);
  const isError = employeeQuery.isError ||
    employeesQuery.isError ||
    businessConfigQuery.isError ||
    (isResourceMode ? hydrateResourceAppointmentsQuery.isError : hydrateAppointmentsQuery.isError);

  // Filter appointments based on employee's data
  const employeeAppointments = isResourceMode
    ? resourceAppointmentsMap
    : (currentEmployee?.appointments ?? {});

  const filteredAppointments = useMemo<Appointment[]>(() => {
    if (isResourceMode) {
      return resourceAppointmentIds
        .map((id) => queryClient.getQueryData(appointmentKey(WEBSITE_ID, id)) as Appointment | undefined)
        .filter((appointment): appointment is Appointment => appointment !== undefined);
    }
    const appointmentIds = new Set(Object.values(employeeAppointments).flat());
    return (myAppointments ?? []).filter((appointment) => appointmentIds.has(appointment.id));
  }, [
    employeeAppointments,
    hydrateResourceAppointmentsQuery.dataUpdatedAt,
    isResourceMode,
    myAppointments,
    queryClient,
    resourceAppointmentIds,
  ]);

  // Loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Error handling
  if (isError) {
    const errorMessage = employeeQuery.error?.message || hydrateAppointmentsQuery.error?.message
    return <Typography color="error" align="center">Failed to load data: {errorMessage}</Typography>;
  }

  return (
    <Box sx={{}}>
      <MyAppointmentsSection
        appointments={filteredAppointments}
        employeeAppointmentsMap={employeeAppointments}
      />
    </Box>
  );
};

export default MyAppointmentsScreen;
